import * as React from 'react';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import {
  format, subDays, addDays,
  startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  subMonths, startOfYear, endOfYear,
} from 'date-fns';

import { cn } from '@/lib/utils';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

// ---- YYYY-MM-DD <-> Date helpers (timezone-safe, local) ----
const toYMD = (d) => (d ? format(d, 'yyyy-MM-dd') : '');
const fromYMD = (s) => {
  if (!s) return undefined;
  const [y, m, day] = String(s).slice(0, 10).split('-').map(Number);
  if (!y || !m || !day) return undefined;
  return new Date(y, m - 1, day);
};
const prettify = (s) => {
  const d = fromYMD(s);
  return d ? format(d, 'dd MMM yyyy') : '';
};

/**
 * Single-date picker. value/onChange use 'YYYY-MM-DD' strings to drop in for
 * <input type="date">. Includes ERP-style quick actions.
 */
export function DatePickerPro({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled = false,
  className,
  allowClear = true,
  'data-testid': testId,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = fromYMD(value);

  const pick = (d) => {
    onChange(toYMD(d));
    setOpen(false);
  };

  const quick = [
    ['Today', () => pick(new Date())],
    ['Yesterday', () => pick(subDays(new Date(), 1))],
    ['Tomorrow', () => pick(addDays(new Date(), 1))],
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm',
            'text-left transition-colors hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
            disabled && 'cursor-not-allowed opacity-50',
            className,
          )}
        >
          <CalendarIcon size={15} className="shrink-0 text-slate-400" />
          <span className={cn('flex-1 truncate', !value && 'text-slate-400')}>
            {value ? prettify(value) : placeholder}
          </span>
          {allowClear && value && !disabled && (
            <span
              role="button"
              tabIndex={0}
              data-testid={testId ? `${testId}-clear` : undefined}
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={13} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-wrap gap-1.5 border-b border-slate-100 p-2">
          {quick.map(([label, fn]) => (
            <button
              key={label}
              type="button"
              onClick={fn}
              data-testid={testId ? `${testId}-quick-${label.toLowerCase()}` : undefined}
              className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
            >
              {label}
            </button>
          ))}
        </div>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => d && pick(d)}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Date-range picker. value = { from, to } as 'YYYY-MM-DD' strings.
 * onChange({ from, to }). Includes ERP-style range presets.
 */
export function DateRangePicker({
  from,
  to,
  onChange,
  placeholder = 'Select date range',
  disabled = false,
  className,
  'data-testid': testId,
}) {
  const [open, setOpen] = React.useState(false);
  const range = { from: fromYMD(from), to: fromYMD(to) };

  const emit = (r) => onChange({ from: toYMD(r?.from), to: toYMD(r?.to) });

  const applyPreset = (f, t) => {
    emit({ from: f, to: t });
    setOpen(false);
  };

  const now = new Date();
  const presets = [
    ['Today', now, now],
    ['Yesterday', subDays(now, 1), subDays(now, 1)],
    ['This Week', startOfWeek(now, { weekStartsOn: 1 }), endOfWeek(now, { weekStartsOn: 1 })],
    ['This Month', startOfMonth(now), endOfMonth(now)],
    ['Last Month', startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
    ['This Year', startOfYear(now), endOfYear(now)],
  ];

  const label = from
    ? `${prettify(from)}${to && to !== from ? ` – ${prettify(to)}` : ''}`
    : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm',
            'text-left transition-colors hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
            disabled && 'cursor-not-allowed opacity-50',
            className,
          )}
        >
          <CalendarIcon size={15} className="shrink-0 text-slate-400" />
          <span className={cn('flex-1 truncate', !from && 'text-slate-400')}>
            {label || placeholder}
          </span>
          {(from || to) && !disabled && (
            <span
              role="button"
              tabIndex={0}
              data-testid={testId ? `${testId}-clear` : undefined}
              onClick={(e) => { e.stopPropagation(); onChange({ from: '', to: '' }); }}
              className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={13} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="flex w-32 shrink-0 flex-col gap-1 border-r border-slate-100 p-2">
            {presets.map(([lbl, f, t]) => (
              <button
                key={lbl}
                type="button"
                onClick={() => applyPreset(f, t)}
                data-testid={testId ? `${testId}-preset-${lbl.toLowerCase().replace(/\s+/g, '-')}` : undefined}
                className="rounded-md px-2.5 py-1.5 text-left text-xs font-medium text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
              >
                {lbl}
              </button>
            ))}
          </div>
          <Calendar
            mode="range"
            numberOfMonths={2}
            selected={range}
            defaultMonth={range.from}
            onSelect={(r) => emit(r || { from: undefined, to: undefined })}
            initialFocus
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
